import { ColumnDef, ColumnFiltersState, Table } from '@tanstack/react-table';
import { parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useMemo, useState } from 'react';
import { LuSearch } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { LifecycleStatus, PortfolioEngineAccount, TradingStatus } from '@ndvr/portfolio-api';

import { BatchInvestmentRequestItem, FilterType } from '@/cip/_openapi/portfolio-optimizer';
import { LifestyleProtectionBadge } from '@/components/LifestyleProtectionBadge';
import { Sidebar } from '@/components/Sidebar';
import { ClientDataTable } from '@/components/table/DataTable';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Page, PageContent, PageTitle, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { formatCurrency } from 'utils/number-formatter';
import { accountRoute } from 'utils/routes';
import { formatAccountType, titleCase } from 'utils/string-utils';
import { SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH, useAiChatSidebarState } from 'views/ai-chat/ai-chat-state';
import { useTenantScopeLabel } from 'views/auth/tenant-selection';
import { useCreateInvestmentRequestsBatch } from 'views/portfolio-engine/investment-request/investment-request-queries';
import {
    useBulkSetAccountsTradeStatus,
    useListPortfolioEngineAccountsForTenants,
} from 'views/portfolio/account-queries';
import { Toast } from 'widgets';

import { AccountOverviewSidebar } from './AccountOverviewSidebar';
import { AccountAssessmentBadges } from './AssessmentBadge';
import {
    AssessmentFilter,
    AssessmentFilterKey,
    formatAssessmentFilterCount,
    matchesAssessmentFilter,
} from './AssessmentFilter';
import { OnboardingWizardModal } from './onboarding-wizard/OnboardingWizardModal';
import { TradeRequestConfirmDialog } from './TradeRequestConfirmDialog';
import { TradeStatusAction, TradeStatusConfirmDialog } from './TradeStatusConfirmDialog';
import { getTradingStatusLabel, TRADING_STATUS_ORDER } from './trading-status-display';
import { UrgentWithdrawalDialog } from './UrgentWithdrawalDialog';

const shouldShowTargetAllocation = (account: PortfolioEngineAccount) =>
    account.tradingStatus !== TradingStatus.MANUAL && account.tradingStatus !== TradingStatus.EXTERNAL;

const getVisibleTargetAllocation = (account: PortfolioEngineAccount) =>
    shouldShowTargetAllocation(account)
        ? (account.portfolioAllocationName ?? account.accountAllocationName)
        : undefined;

export const AllAccountsPage = () => {
    const { isOpen: isAiSidebarOpen, sidebarWidth, setWidth } = useAiChatSidebarState();
    const { data: accounts = [], isLoading } = useListPortfolioEngineAccountsForTenants(LifecycleStatus.REAL);
    const tenantScopeLabel = useTenantScopeLabel();
    const [{ q: searchTerm, assessment: assessmentFilter }, setFilters] = useQueryStates(
        {
            q: parseAsString.withDefault(''),
            assessment: parseAsString.withDefault('all'),
        },
        { history: 'replace' },
    );
    const assessmentFilterKey = assessmentFilter as AssessmentFilterKey;
    const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(new Set());
    // Filter changes can hide currently-selected accounts, leaving the user
    // unable to uncheck them. Reset selection whenever any filter changes.
    const clearSelection = useCallback(() => setSelectedAccountIds(new Set()), []);
    const setSearchTerm = (value: string) => {
        clearSelection();
        setFilters({ q: value || null });
    };
    const setAssessmentFilter = (value: AssessmentFilterKey) => {
        clearSelection();
        setFilters({ assessment: value === 'all' ? null : value });
    };
    const [columnFilters, setColumnFiltersState] = useState<ColumnFiltersState>([]);
    const setColumnFilters = useCallback<typeof setColumnFiltersState>(
        (updater) => {
            clearSelection();
            setColumnFiltersState(updater);
        },
        [clearSelection],
    );
    const tradingStatusFilterSet = useMemo<Set<TradingStatus>>(
        () =>
            new Set((columnFilters.find((f) => f.id === 'tradingStatus')?.value as TradingStatus[] | undefined) ?? []),
        [columnFilters],
    );
    const toggleTradingStatusFilter = useCallback(
        (status: TradingStatus) => {
            setColumnFilters((prev) => {
                const others = prev.filter((f) => f.id !== 'tradingStatus');
                const current =
                    (prev.find((f) => f.id === 'tradingStatus')?.value as TradingStatus[] | undefined) ?? [];
                const next = current.includes(status) ? current.filter((s) => s !== status) : [...current, status];
                return next.length ? [...others, { id: 'tradingStatus', value: next }] : others;
            });
        },
        [setColumnFilters],
    );
    const [urgentWithdrawalAccount, setUrgentWithdrawalAccount] = useState<PortfolioEngineAccount | null>(null);

    const [tradeStatusConfirm, setTradeStatusConfirm] = useState<{
        accounts: PortfolioEngineAccount[];
        action: TradeStatusAction;
        totalSelectedCount: number;
    } | null>(null);

    const [tradeRequestAccounts, setTradeRequestAccounts] = useState<PortfolioEngineAccount[] | null>(null);
    const [tradeRequestWarnings, setTradeRequestWarnings] = useState<Record<string, string>>({});

    const [onboardingAccounts, setOnboardingAccounts] = useState<PortfolioEngineAccount[] | null>(null);

    const { mutate: setAccountsTradeStatus, isPending: isUpdatingTradeStatus } = useBulkSetAccountsTradeStatus();
    const { mutate: createInvestmentRequestsBatch, isPending: isCreatingTradeRequests } =
        useCreateInvestmentRequestsBatch();

    const submitTradeRequests = (items: BatchInvestmentRequestItem[]) => {
        setTradeRequestWarnings({});
        createInvestmentRequestsBatch(
            { batchInvestmentRequestCreatePayload: { requests: items } },
            {
                onSuccess: (response) => {
                    const warnings = response?.warnings ?? {};
                    const warningCount = Object.keys(warnings).length;
                    if (warningCount === 0) {
                        toast.success(
                            items.length === 1 ? 'Trade request submitted' : `${items.length} trade requests submitted`,
                        );
                        setTradeRequestAccounts(null);
                        return;
                    }
                    const submitted = items.length - warningCount;
                    toast.info(
                        submitted > 0
                            ? `${submitted} of ${items.length} trade requests submitted — see warnings`
                            : 'Trade requests not submitted — see warnings',
                    );
                    setTradeRequestWarnings(warnings);
                },
                onError: (error) => {
                    Toast.forError(error, 'Failed to submit trade requests');
                },
            },
        );
    };

    const openUrgentWithdrawal = (accountGUID: string) => {
        const account = accounts.find((a) => a.accountGUID === accountGUID);
        if (!account) return;
        setUrgentWithdrawalAccount(account);
    };

    const filteredAccounts = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return accounts.filter((account) => {
            if (!matchesAssessmentFilter(account, assessmentFilterKey)) return false;
            if (!term) return true;
            return (
                account.accountNamePlain?.toLowerCase().includes(term) ||
                account.accountGUID.toLowerCase().includes(term) ||
                account.custodyAccountId?.toLowerCase().includes(term)
            );
        });
    }, [accounts, searchTerm, assessmentFilterKey]);

    const countText = useMemo(() => {
        if (isLoading) return null;
        const base = formatAssessmentFilterCount(assessmentFilterKey, filteredAccounts.length);
        return tenantScopeLabel ? `${base} · ${tenantScopeLabel}` : base;
    }, [isLoading, filteredAccounts.length, assessmentFilterKey, tenantScopeLabel]);

    const toggleAccountSelected = useCallback((accountId: string, checked: boolean) => {
        setSelectedAccountIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(accountId);
            } else {
                next.delete(accountId);
            }
            return next;
        });
    }, []);

    const columns = useMemo<ColumnDef<PortfolioEngineAccount>[]>(
        () => [
            {
                id: 'select',
                enableResizing: false,
                header: ({ table }) => {
                    // Derive from the table's filtered+sorted row model so column filters
                    // (e.g. Trading Status) don't cause us to select hidden accounts.
                    const visibleIds = table.getRowModel().rows.map((r) => r.original.accountGUID);
                    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedAccountIds.has(id));
                    const someSelected = visibleIds.some((id) => selectedAccountIds.has(id));
                    return (
                        <Checkbox
                            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                            onCheckedChange={(checked) =>
                                setSelectedAccountIds((prev) => {
                                    const next = new Set(prev);
                                    visibleIds.forEach((id) => (checked === true ? next.add(id) : next.delete(id)));
                                    return next;
                                })
                            }
                            aria-label="Select all accounts"
                        />
                    );
                },
                cell: ({ row }) => (
                    <Checkbox
                        checked={selectedAccountIds.has(row.original.accountGUID)}
                        onCheckedChange={(checked) => toggleAccountSelected(row.original.accountGUID, checked === true)}
                        aria-label={`Select ${row.original.accountNamePlain ?? row.original.accountGUID}`}
                        onClick={(e) => e.stopPropagation()}
                    />
                ),
                enableSorting: false,
                size: 32,
            },
            {
                accessorKey: 'accountNamePlain',
                header: 'Account',
                meta: { filterType: FilterType.STRING },
                cell: ({ row }) => {
                    const account = row.original;
                    const subParts = [
                        account.custodyAccountId,
                        account.custodyType ? titleCase(account.custodyType) : null,
                        formatAccountType(account.accountType),
                    ].filter(Boolean);
                    return (
                        <div className="flex min-w-0 flex-col">
                            <Link
                                to={accountRoute(account.accountGUID)}
                                className="text-gold-400 hover:text-gold-500 w-fit max-w-full truncate font-medium hover:underline"
                            >
                                {account.accountNamePlain ?? '—'}
                            </Link>
                            {subParts.length > 0 && (
                                <span className="text-cocoa-500 truncate text-xs">{subParts.join(' · ')}</span>
                            )}
                        </div>
                    );
                },
                enableSorting: true,
            },
            {
                id: 'targetAllocation',
                accessorFn: getVisibleTargetAllocation,
                header: () => <span className="pl-5">Target Allocation</span>,
                meta: {
                    filterType: FilterType.LIST,
                    filterOptionsFn: (table?: Table<PortfolioEngineAccount>) => {
                        const set = new Set<string>();
                        table?.getCoreRowModel().rows.forEach((r) => {
                            const v = getVisibleTargetAllocation(r.original);
                            if (v) set.add(v);
                        });
                        return Array.from(set).sort();
                    },
                },
                cell: ({ row }) => {
                    const account = row.original;
                    const showTargetAllocation = shouldShowTargetAllocation(account);
                    const label = getVisibleTargetAllocation(account) ?? '—';
                    const hasPortfolioLevelAllocation = showTargetAllocation && !!account.portfolioAllocationName;
                    const hasLifestyleProtection = showTargetAllocation && Number(account.initialSecuredness) > 0;
                    return (
                        <div className="relative flex min-w-0 flex-col items-start pl-5">
                            {hasLifestyleProtection && (
                                <span className="absolute top-1/2 left-0 -translate-y-1/2">
                                    <LifestyleProtectionBadge />
                                </span>
                            )}
                            <span className="max-w-full truncate">{label}</span>
                            {hasPortfolioLevelAllocation && (
                                <Badge
                                    variant="outline"
                                    className="border-cocoa-500 text-cocoa-600 mt-1 rounded px-1.5 py-0.5"
                                >
                                    Portfolio-level
                                </Badge>
                            )}
                        </div>
                    );
                },
                enableSorting: true,
                sortUndefined: 'last',
            },
            {
                id: 'assessment',
                header: 'Assessment',
                cell: ({ row }) => <AccountAssessmentBadges account={row.original} />,
                enableSorting: false,
            },
            {
                id: 'tradingStatus',
                accessorFn: (account) => account.tradingStatus,
                header: 'Trading Status',
                cell: ({ row }) => getTradingStatusLabel(row.original.tradingStatus),
                enableSorting: true,
                sortingFn: (a, b) =>
                    getTradingStatusLabel(a.original.tradingStatus).localeCompare(
                        getTradingStatusLabel(b.original.tradingStatus),
                    ),
                meta: {
                    filterType: FilterType.LIST,
                    formatOption: (value) => getTradingStatusLabel(value as TradingStatus),
                    filterOptionsFn: () => [...TRADING_STATUS_ORDER],
                },
            },
            {
                id: 'aum',
                accessorFn: (account) => (account.aum != null ? Number(account.aum) : undefined),
                header: 'Value',
                cell: ({ row }) => <span className="whitespace-nowrap">{formatCurrency(row.original.aum)}</span>,
                enableSorting: true,
                sortUndefined: 'last',
                meta: { filterType: FilterType.NUMBER },
            },
            {
                id: 'cash',
                accessorFn: (account) => (account.cash != null ? Number(account.cash) : undefined),
                header: 'Cash',
                cell: ({ row }) => <span className="whitespace-nowrap">{formatCurrency(row.original.cash)}</span>,
                enableSorting: true,
                sortUndefined: 'last',
                meta: { filterType: FilterType.NUMBER },
            },
        ],
        [selectedAccountIds, toggleAccountSelected],
    );

    return (
        <Page>
            <PageTopBar />
            <PageContent scroll="children" padding="none">
                <div className="flex min-h-0 flex-1 overflow-hidden">
                    <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
                        <PageTitleRow
                            actions={
                                <InputGroup className="w-64 rounded-full">
                                    <InputGroupInput
                                        type="search"
                                        placeholder="Search accounts..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <InputGroupAddon align="inline-end">
                                        <LuSearch />
                                    </InputGroupAddon>
                                </InputGroup>
                            }
                        >
                            <div className="flex flex-col">
                                <PageTitle>Accounts</PageTitle>
                                {countText ? (
                                    <p className="text-cocoa-600 -mt-1 text-sm font-thin">{countText}</p>
                                ) : null}
                            </div>
                        </PageTitleRow>
                        <div className="px-6 pb-2">
                            <AssessmentFilter
                                accounts={accounts}
                                value={assessmentFilterKey}
                                onChange={setAssessmentFilter}
                            />
                        </div>
                        <div className="min-h-0 flex-1 overflow-auto">
                            <div className="flex flex-col px-6 pb-6">
                                <ClientDataTable
                                    data={filteredAccounts}
                                    columns={columns}
                                    isLoading={isLoading}
                                    truncateCells
                                    state={{ columnFilters }}
                                    onColumnFiltersChange={setColumnFilters}
                                    initialState={{ sorting: [{ id: 'aum', desc: true }] }}
                                    rowClassName={(account) =>
                                        selectedAccountIds.has(account.accountGUID)
                                            ? 'bg-cocoa-100/50 hover:!bg-cocoa-100/70'
                                            : ''
                                    }
                                />
                            </div>
                        </div>
                    </main>
                    <Sidebar
                        side="right"
                        isOpen={!isAiSidebarOpen}
                        initialWidth={sidebarWidth}
                        minWidth={SIDEBAR_MIN_WIDTH}
                        maxWidth={SIDEBAR_MAX_WIDTH}
                        onWidthChange={setWidth}
                        contentClassName="overflow-auto px-6 py-4 bg-white"
                    >
                        <AccountOverviewSidebar
                            accounts={accounts}
                            selectedAccountIds={selectedAccountIds}
                            tradingStatusFilter={tradingStatusFilterSet}
                            onToggleTradingStatusFilter={toggleTradingStatusFilter}
                            assessmentFilter={assessmentFilterKey}
                            onSelectAssessmentFilter={setAssessmentFilter}
                            onTradeRequest={(targetAccounts) => setTradeRequestAccounts(targetAccounts)}
                            onOnboard={(targetAccounts) => setOnboardingAccounts(targetAccounts)}
                            onUrgentWithdrawal={openUrgentWithdrawal}
                            onSetTradingStatus={(targetAccounts, action, totalSelectedCount) =>
                                setTradeStatusConfirm({ accounts: targetAccounts, action, totalSelectedCount })
                            }
                        />
                    </Sidebar>
                </div>
            </PageContent>
            {onboardingAccounts && (
                <OnboardingWizardModal accounts={onboardingAccounts} onClose={() => setOnboardingAccounts(null)} />
            )}
            {urgentWithdrawalAccount && (
                <UrgentWithdrawalDialog
                    account={urgentWithdrawalAccount}
                    onClose={() => setUrgentWithdrawalAccount(null)}
                />
            )}
            {tradeRequestAccounts && (
                <TradeRequestConfirmDialog
                    accounts={tradeRequestAccounts}
                    warnings={tradeRequestWarnings}
                    isPending={isCreatingTradeRequests}
                    onClose={() => {
                        setTradeRequestAccounts(null);
                        setTradeRequestWarnings({});
                    }}
                    onConfirm={submitTradeRequests}
                />
            )}
            {tradeStatusConfirm && (
                <TradeStatusConfirmDialog
                    accounts={tradeStatusConfirm.accounts}
                    action={tradeStatusConfirm.action}
                    totalSelectedCount={tradeStatusConfirm.totalSelectedCount}
                    isPending={isUpdatingTradeStatus}
                    onClose={() => setTradeStatusConfirm(null)}
                    onConfirm={() =>
                        setAccountsTradeStatus(
                            {
                                accountGUIDs: tradeStatusConfirm.accounts.map((a) => a.accountGUID),
                                action: tradeStatusConfirm.action,
                            },
                            { onSuccess: () => setTradeStatusConfirm(null) },
                        )
                    }
                />
            )}
        </Page>
    );
};
